import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { useStores } from "../../../use-stores";
import {
  PodCatalogUpsertBody,
  PodCatalogVariantUpsert,
  buildPodVariantSku,
  extractPodCatalogImageUrls,
  resolvePodCatalogVariantAccess,
} from "../../../../../models/Products/PodCatalog";
import { useNotification } from "../../../../../utils/NotificationsContext";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import PodProductVariantsDrawer from "../../components/product/PodProductVariantsDrawer";
import PodProductMediaAssetsCard from "../../components/product/PodProductMediaAssetsCard";
import ImageAPI from "../../../api/ImageAPI";
import ProductIcon from "../../../../../assets/productIcon.png";
import BusinessIcon from "../../../../../assets/business_building.png";
import RupeeIcon from "../../../../../assets/rupeeicon.png";
import EyeIcon from "../../../../../assets/eyeframe.png";

const PodCatalogLiteDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { podCatalogStore, productStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const theme = useTheme();
  const numericProductId = Number(productId);

  const { canView: canViewPodVariants, canEdit: canEditPodVariants } =
    resolvePodCatalogVariantAccess(podCatalogStore.detail, (role) =>
      userStore.hasPermission(role)
    );

  const [infoCardOpen, setInfoCardOpen] = useState(true);
  const [variantsDrawerOpen, setVariantsDrawerOpen] = useState(false);
  const [mediaSaving, setMediaSaving] = useState(false);
  const [mediaExistingUrls, setMediaExistingUrls] = useState<string[]>([]);
  const [mediaNewFiles, setMediaNewFiles] = useState<File[]>([]);

  const podVariantRows = useMemo<PodCatalogVariantUpsert[]>(() => {
    const rows: PodCatalogVariantUpsert[] = [];
    for (const variant of podCatalogStore.detail?.pod_catalog_variants || []) {
      const variantId = Number(variant.variant_id ?? variant.id ?? 0);
      if (!(variantId > 0)) continue;
      rows.push({
        id: variant.id,
        variant_id: variantId,
        color: variant.color || "",
        sku: variant.sku || "",
        variant_sku: variant.variant_sku || "",
      });
    }
    return rows;
  }, [podCatalogStore.detail?.pod_catalog_variants]);

  const getPodVariantChipLabel = (row: PodCatalogVariantUpsert) => {
    if (row.variant_sku?.trim()) return row.variant_sku.trim();
    if (row.sku?.trim()) return row.sku.trim();
    if (numericProductId > 0 && row.sku?.trim()) {
      return buildPodVariantSku(numericProductId, row.sku, row.color);
    }
    return `Variant #${row.variant_id}`;
  };

  const initialMediaUrls = useMemo(
    () => extractPodCatalogImageUrls(podCatalogStore.detail?.pod_catalog_images),
    [podCatalogStore.detail?.pod_catalog_images]
  );

  useEffect(() => {
    setMediaExistingUrls(initialMediaUrls);
    setMediaNewFiles([]);
  }, [initialMediaUrls]);

  const handleSubmitVariants = async (variants: PodCatalogVariantUpsert[]) => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;
    if (!canEditPodVariants) return;
    await podCatalogStore.upsertPodCatalogVariants(numericProductId, variants, {
      allowEdit: canEditPodVariants,
    });
  };

  useEffect(() => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;

    podCatalogStore.fetchPodCatalogDetail(numericProductId).catch(() => {});

    return () => podCatalogStore.clearPodCatalogDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericProductId]);

  const handleUpdate = async () => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;
    const payload: PodCatalogUpsertBody = {};

    const trimmedSku = podCatalogStore.detailProductSku.trim();
    if (trimmedSku !== podCatalogStore.detailInitialProductSku.trim()) {
      payload.product_sku = trimmedSku;
    }

    const trimmedPodPrice = podCatalogStore.detailPodPrice.trim();
    if (trimmedPodPrice !== podCatalogStore.detailInitialPodPrice.trim()) {
      const parsedPodPrice = Number(trimmedPodPrice);
      if (!Number.isFinite(parsedPodPrice) || parsedPodPrice < 0) {
        showError("Error occurred", "POD price must be a valid non-negative number.");
        return;
      }
      payload.swageazy_selling_price = parsedPodPrice;
    }

    if (Object.keys(payload).length === 0) return;

    podCatalogStore.setDetailSaving(true);
    try {
      const res = await podCatalogStore.upsertPodCatalogDetail(
        numericProductId,
        payload
      );
      showSuccess(res?.message || "Product updated successfully");
    } catch (err: any) {
      showError(
        "Error occurred",
        err?.response?.data?.message || err?.message || "Unable to update POD product."
      );
    } finally {
      podCatalogStore.setDetailSaving(false);
    }
  };

  const handleAddToPod = async () => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;
    podCatalogStore.setDetailAddingToPod(true);
    try {
      const res = await podCatalogStore.addToPod(numericProductId);
      if (res?.success) {
        showSuccess(res?.message || "Product added to POD successfully");
        productStore.setProductPodCatalogStatus(numericProductId, true);
        await podCatalogStore.fetchPodCatalogDetail(numericProductId);
        return;
      }
      showError("Error occurred", res?.message || "Unable to add to POD.");
    } catch (err: any) {
      showError(
        "Error occurred",
        err?.response?.data?.message || err?.message || "Unable to add to POD."
      );
    } finally {
      podCatalogStore.setDetailAddingToPod(false);
    }
  };

  const handleRemoveFromPod = async () => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;
    podCatalogStore.setDetailRemovingFromPod(true);
    try {
      const res = await podCatalogStore.removeFromPod(numericProductId);
      if (res?.success) {
        showSuccess(res?.message || "Removed from POD successfully");
        podCatalogStore.setIsRemovedFromPod(true);
        return;
      }
      showError("Error occurred", res?.message || "Unable to remove from POD.");
    } catch (err: any) {
      showError(
        "Error occurred",
        err?.response?.data?.message || err?.message || "Unable to remove from POD."
      );
    } finally {
      podCatalogStore.setDetailRemovingFromPod(false);
    }
  };

  const handleCloseVariantsDrawer = () => {
    setVariantsDrawerOpen(false);
    podCatalogStore.clearDetailVariantDrawer();
  };

  const handleSavePodMedia = async () => {
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;
    setMediaSaving(true);
    try {
      let mergedUrls = [...mediaExistingUrls];
      if (mediaNewFiles.length > 0) {
        const uploaded = await new ImageAPI().uploadImage(mediaNewFiles, "product-images");
        mergedUrls = [...mergedUrls, ...uploaded];
      }
      const uniqueUrls = Array.from(new Set(mergedUrls.filter((url) => Boolean(url))));
      const res = await podCatalogStore.savePodCatalogMedia(numericProductId, uniqueUrls);
      setMediaExistingUrls(uniqueUrls);
      setMediaNewFiles([]);
      showSuccess(res?.message || "Media updated successfully");
    } catch (err: any) {
      showError(
        "Error occurred",
        err?.response?.data?.message || err?.message || "Unable to update media."
      );
    } finally {
      setMediaSaving(false);
    }
  };

  const hasMediaChanges =
    mediaNewFiles.length > 0 ||
    JSON.stringify(mediaExistingUrls) !== JSON.stringify(initialMediaUrls);

  return (
    <Box
      sx={{
        marginLeft: 2,
        marginRight: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.customColors.white,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            paddingLeft: 2,
            paddingTop: 2,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            color: theme.palette.customColors.greyTextColor,
            maxWidth: "3.75rem",
            "&:hover, &:active": {
              color: theme.palette.customColors.greyTextColor,
            },
          }}
          onClick={() => navigate("/products/listing?pod=1")}
        >
          <ArrowBackIcon sx={{ fontSize: "1rem" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>Back</span>
        </Box>

        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Box>
            <CustomTypography variant="heading4" style={{ marginTop: 16 }}>
              Update Product
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginTop: 5, marginBottom: 12 }}
            >
              Create and manage your product information with ease.
            </CustomTypography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Tabs
              value={1}
              onChange={(_, tab) => {
                if (tab === 0 && numericProductId > 0) {
                  navigate(`/products/${numericProductId}`);
                }
              }}
              sx={{
                mb: 1,
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "auto",
                flex: 1,
                "& .MuiTab-root": { py: 1, minHeight: "auto" },
              }}
            >
              <Tab label="Product Detail" />
              <Tab label="POD Product Detail" />
            </Tabs>
            {podCatalogStore.detailNotFound ? (
              <AppButton
                label={
                  podCatalogStore.detailAddingToPod ? "Adding..." : "Add to POD"
                }
                variant="primary"
                onClick={handleAddToPod}
                disabled={podCatalogStore.detailAddingToPod}
                customStyles={{ minWidth: 130 }}
              />
            ) : (
              <AppButton
                label={
                  podCatalogStore.detailRemovingFromPod
                    ? "Removing..."
                    : "Remove from POD"
                }
                variant="primary"
                onClick={handleRemoveFromPod}
                disabled={
                  podCatalogStore.detailRemovingFromPod ||
                  podCatalogStore.isRemovedFromPod
                }
                customStyles={{
                  minWidth: 130,
                  opacity:
                    podCatalogStore.detailRemovingFromPod ||
                    podCatalogStore.isRemovedFromPod
                      ? 0.6
                      : 1,
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {podCatalogStore.detailLoading && (
        <Box
          sx={{
            minHeight: "40vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!podCatalogStore.detailLoading && podCatalogStore.detailNotFound && (
        <Box sx={{ px: 2, pb: 4 }}>
          <Card
            sx={{
              mt: 2,
              border: `1px solid ${theme.palette.customColors.greyBackground}`,
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{
                minHeight: "36vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 1.5,
                py: 6,
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: theme.palette.customColors.greyBackground,
                }}
              >
                <Inventory2OutlinedIcon
                  sx={{
                    fontSize: 32,
                    color: theme.palette.customColors.greyTextColor,
                  }}
                />
              </Box>
              <CustomTypography variant="heading6">
                This product is not in the POD catalog
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors.greyTextColor}
                style={{ maxWidth: 400 }}
              >
                Use Add to POD above to publish it.
              </CustomTypography>
            </CardContent>
          </Card>
        </Box>
      )}

      {!podCatalogStore.detailLoading && podCatalogStore.detailError && (
        <Box sx={{ px: 2, pb: 3 }}>
          <CustomTypography variant="body2">
            {podCatalogStore.detailError}
          </CustomTypography>
        </Box>
      )}

      {!podCatalogStore.detailLoading &&
        !podCatalogStore.detailError &&
        !podCatalogStore.detailNotFound &&
        podCatalogStore.detail && (
          <>
            <Card sx={{ marginTop: 2, marginBottom: 2 }}>
              <CardHeader
                onClick={() => setInfoCardOpen((open) => !open)}
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src={ProductIcon}
                      alt="Product"
                      style={{ width: 30, height: 30 }}
                    />
                    <CustomTypography variant="heading5">
                      POD Product Information
                    </CustomTypography>
                    <Chip
                      label="Core Details"
                      size="small"
                      sx={{
                        ml: 1,
                        borderRadius: 2,
                        backgroundColor: theme.palette.customColors.white,
                        color: theme.palette.customColors.greyTextColor,
                        border: `1px solid ${theme.palette.customColors.greyBackground}`,
                        height: "20px",
                        fontSize: "0.65rem",
                        "& .MuiChip-label": { px: 0.8, py: 0 },
                      }}
                    />
                  </Box>
                }
                action={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                      <CustomTypography variant="body2" color="text.secondary">
                        4 fields
                      </CustomTypography>
                    </Box>
                    <IconButton>
                      {infoCardOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                }
                sx={{
                  cursor: "pointer",
                  px: 3,
                  py: 2,
                  borderBottom: `1px solid ${theme.palette.customColors.greyBackground}`,
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.greyBackground,
                  },
                }}
              />

              <Collapse in={infoCardOpen} timeout="auto" unmountOnExit>
                <CardContent sx={{ px: 4 }}>
                  <Box
                    mt={1}
                    display="grid"
                    gridTemplateColumns={{
                      xs: "1fr",
                      lg: canViewPodVariants ? "7fr 1px 4fr" : "1fr",
                    }}
                    alignItems="stretch"
                    gap={4}
                  >
                    {canViewPodVariants && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      sx={{ height: "100%", minHeight: 0 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <img
                          src={EyeIcon}
                          alt="Variants"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">Variants </CustomTypography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          width: "100%",
                        }}
                      >
                      {podVariantRows.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                          {podVariantRows.map((row) => {
                            const chipKey =
                              row.id ??
                              (row.variant_id != null
                                ? `variant-${row.variant_id}`
                                : `row-${row.color}-${row.variant_sku}-${row.sku}`);
                            return (
                              <Box key={`pod-variant-chip-${chipKey}`}>
                                <Chip
                                  sx={{ margin: 1 }}
                                  size="small"
                                  label={getPodVariantChipLabel(row)}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            flex: { xs: "0 0 auto", lg: 1 },
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            minHeight: { xs: 100, lg: 0 },
                            py: 3,
                            px: 2,
                            borderRadius: 1,
                            border: `1px dashed ${theme.palette.customColors.greyBackground}`,
                            bgcolor: theme.palette.customColors.white,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            color={theme.palette.customColors.greyTextColor}
                            style={{ textAlign: "center" }}
                          >
                            {canEditPodVariants
                              ? "No variants yet. Use Manage Variants to add one."
                              : "No variants configured."}
                          </CustomTypography>
                        </Box>
                      )}
                      </Box>
                      {canEditPodVariants && (
                      <Box sx={{ display: { xs: "block", lg: "none" }, alignSelf: "flex-end" }}>
                        <AppButton
                          label="Manage Variants"
                          variant="secondary"
                          onClick={() => setVariantsDrawerOpen(true)}
                          customStyles={{ minWidth: 150 }}
                        />
                      </Box>
                      )}
                    </Box>
                    )}

                    {canViewPodVariants && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ display: { xs: "none", lg: "block" } }}
                    />
                    )}

                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            height: 2,
                            flex: 1,
                            backgroundColor: theme.palette.customColors.greyBackground,
                          }}
                        />
                        <CustomTypography variant="heading8">Pricing</CustomTypography>
                        <Box
                          sx={{
                            height: 2,
                            flex: 1,
                            backgroundColor: theme.palette.customColors.greyBackground,
                          }}
                        />
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <img
                          src={RupeeIcon}
                          alt="Cost Price"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">Cost Price</CustomTypography>
                      </Box>
                      <FormTextField
                        id="pod-cost"
                        type="number"
                        placeholder="0.00"
                        value={podCatalogStore.detail.cost_price ?? ""}
                        disabled
                        fontSize="14px"
                        marginBottom={0}
                        borderColor={theme.palette.customColors.greyBackground}
                      />

                      <Box display="flex" alignItems="center" gap={1}>
                        <img
                          src={RupeeIcon}
                          alt="Selling Price"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">Selling Price</CustomTypography>
                      </Box>
                      <FormTextField
                        id="pod-selling"
                        type="number"
                        placeholder="0.00"
                        value={podCatalogStore.detailPodPrice}
                        onChange={(_, value) =>
                          podCatalogStore.setDetailPodPrice(String(value || ""))
                        }
                        fontSize="14px"
                        marginBottom={0}
                        borderColor={theme.palette.customColors.greyBackground}
                      />

                      <Box display="flex" alignItems="center" gap={1}>
                        <img src={BusinessIcon} alt="SKU" style={{ width: 17, height: 17 }} />
                        <CustomTypography variant="heading8">SKU</CustomTypography>
                      </Box>
                      <FormTextField
                        id="pod-sku"
                        placeholder={
                          numericProductId > 0
                            ? `SE-${numericProductId}`
                            : "SE-{productId}"
                        }
                        value={podCatalogStore.detailProductSku}
                        onChange={(_, value) =>
                          podCatalogStore.setDetailProductSku(String(value || ""))
                        }
                        fontSize="14px"
                        marginBottom={0}
                        borderColor={theme.palette.customColors.greyBackground}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      justifyContent: { xs: "flex-end", lg: "space-between" },
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    {canEditPodVariants && (
                    <Box sx={{ display: { xs: "none", lg: "block" } }}>
                      <AppButton
                        label="Manage Variants"
                        variant="secondary"
                        onClick={() => setVariantsDrawerOpen(true)}
                        customStyles={{ minWidth: 150 }}
                      />
                    </Box>
                    )}
                    <Box sx={{ display: "flex", gap: 1.5, ml: { xs: 0, lg: "auto" } }}>
                      <AppButton
                        label="Cancel"
                        variant="grayscale"
                        onClick={() => podCatalogStore.resetDetailFormToInitial()}
                        disabled={podCatalogStore.detailSaving}
                        customStyles={{ minWidth: 120 }}
                      />
                      <AppButton
                        label={
                          podCatalogStore.detailSaving ? "Updating..." : "Update"
                        }
                        variant="primary"
                        onClick={handleUpdate}
                        disabled={
                          podCatalogStore.detailSaving ||
                          !podCatalogStore.hasDetailFormChanges
                        }
                        customStyles={{ minWidth: 120 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            <PodProductMediaAssetsCard
              open={infoCardOpen}
              onToggle={() => setInfoCardOpen((open) => !open)}
              existingUrls={mediaExistingUrls}
              newFiles={mediaNewFiles}
              initialUrls={initialMediaUrls}
              saving={mediaSaving}
              hasChanges={hasMediaChanges}
              onDeleteExisting={(index) =>
                setMediaExistingUrls((prev) => prev.filter((_, idx) => idx !== index))
              }
              onRemoveNew={(image: { id: string | number; title?: string; img: string }) =>
                setMediaNewFiles((prev) => prev.filter((file) => file.name !== image.title))
              }
              onAddNew={(files: File[]) => setMediaNewFiles((prev) => [...prev, ...files])}
              onReset={() => {
                setMediaExistingUrls(initialMediaUrls);
                setMediaNewFiles([]);
              }}
              onSave={handleSavePodMedia}
            />
          </>
        )}
      <PodProductVariantsDrawer
        open={variantsDrawerOpen}
        onClose={handleCloseVariantsDrawer}
        productName={podCatalogStore.detail?.name || ""}
        productId={numericProductId}
        podCatalogueId={podCatalogStore.detail?.pod_catalogue_id ?? undefined}
        initialRows={podVariantRows}
        isLoading={podCatalogStore.detailLoading}
        canEditVariants={canEditPodVariants}
        onSubmitAll={handleSubmitVariants}
      />
    </Box>
  );
};

export default observer(PodCatalogLiteDetail);
